/**
 * LOCAL PREVIEW STORE
 *
 * Zustand store for local preview server + AI chat (Tauri desktop only).
 * All Tauri API calls use dynamic imports to avoid breaking web builds.
 */

import { create } from "zustand";
import { isTauri } from "@/hooks/usePlatform";
import type { AiProvider } from "@/types/ai-chat";

// ── Types ───────────────────────────────────────────────────────────

export interface StackInfo {
  framework: string;
  dev_command: string;
  default_port: number;
  has_docker_compose: boolean;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

// ── Dynamic Tauri helpers ───────────────────────────────────────────

async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

async function tauriListen(event: string, handler: (payload: unknown) => void): Promise<() => void> {
  const { listen } = await import("@tauri-apps/api/event");
  const unlisten = await listen(event, (e: { payload: unknown }) => handler(e.payload));
  return unlisten;
}

// ── Constants ───────────────────────────────────────────────────────

const MAX_LOGS = 1000;

// ── Store ───────────────────────────────────────────────────────────

interface LocalPreviewState {
  // Server state
  serverRunning: boolean;
  serverPort: number | null;
  detectedStack: StackInfo | null;
  logs: string[];
  serverCommand: string;

  // Chat state
  chatMessages: ChatMessage[];
  chatStreaming: boolean;

  // Actions
  detectStack: (projectPath: string) => Promise<StackInfo>;
  startServer: (projectPath: string, command?: string) => Promise<void>;
  stopServer: () => Promise<void>;
  fetchStatus: () => Promise<void>;
  sendChatMessage: (
    content: string,
    projectPath: string,
    previewUrl?: string,
    provider?: AiProvider,
  ) => Promise<void>;
  clearChat: () => void;
  clearLogs: () => void;

  // Internal
  addLog: (line: string) => void;
}

export const useLocalPreviewStore = create<LocalPreviewState>((set, get) => ({
  serverRunning: false,
  serverPort: null,
  detectedStack: null,
  logs: [],
  serverCommand: "",
  chatMessages: [],
  chatStreaming: false,

  async detectStack(projectPath: string): Promise<StackInfo> {
    if (!isTauri()) throw new Error("Only available in desktop app");
    const stack = await tauriInvoke<StackInfo>("preview_detect_stack", {
      projectPath,
    });
    set({ detectedStack: stack, serverCommand: stack.dev_command });
    return stack;
  },

  async startServer(projectPath: string, command?: string) {
    if (!isTauri()) throw new Error("Only available in desktop app");
    const cmd = command || get().serverCommand || undefined;
    await tauriInvoke("preview_start_server", {
      projectPath,
      command: cmd,
    });
    set({ serverRunning: true, serverPort: null, logs: [] });
  },

  async stopServer() {
    if (!isTauri()) throw new Error("Only available in desktop app");
    await tauriInvoke("preview_stop_server");
    set({ serverRunning: false, serverPort: null });
  },

  async fetchStatus() {
    if (!isTauri()) return;
    const status = await tauriInvoke<{
      running: boolean;
      port: number | null;
      command: string | null;
      project_path: string | null;
    }>("preview_server_status");
    set({
      serverRunning: status.running,
      serverPort: status.port,
      serverCommand: status.command || get().serverCommand,
    });
  },

  async sendChatMessage(
    content: string,
    projectPath: string,
    previewUrl?: string,
    provider: AiProvider = "claude-code",
  ) {
    if (!isTauri()) throw new Error("Only available in desktop app");

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content,
    };

    // Add user message + empty assistant message for streaming
    const assistantMsg: ChatMessage = {
      id: (Date.now() + 1).toString(),
      role: "assistant",
      content: "",
    };

    set((s) => ({
      chatMessages: [...s.chatMessages, userMsg, assistantMsg],
      chatStreaming: true,
    }));

    // Build messages array for API (exclude the empty assistant msg)
    const apiMessages = [...get().chatMessages.slice(0, -1)].map((m) => ({
      role: m.role,
      content: m.content,
    }));

    try {
      await tauriInvoke("preview_chat", {
        messages: apiMessages,
        projectPath,
        previewUrl: previewUrl || null,
        provider,
      });
    } catch (err) {
      // Append error to assistant message
      set((s) => {
        const msgs = [...s.chatMessages];
        const last = msgs[msgs.length - 1];
        if (last?.role === "assistant") {
          msgs[msgs.length - 1] = {
            ...last,
            content: last.content || `Error: ${err}`,
          };
        }
        return { chatMessages: msgs, chatStreaming: false };
      });
    }
  },

  clearChat() {
    set({ chatMessages: [], chatStreaming: false });
  },

  clearLogs() {
    set({ logs: [] });
  },

  addLog(line: string) {
    set((s) => {
      const logs = [...s.logs, line];
      if (logs.length > MAX_LOGS) {
        return { logs: logs.slice(logs.length - MAX_LOGS) };
      }
      return { logs };
    });
  },
}));

// ── Event listener setup ────────────────────────────────────────────

let listenersInitialized = false;

export function initPreviewListeners(): () => void {
  if (!isTauri() || listenersInitialized) return () => {};
  listenersInitialized = true;

  const unlisteners: (() => void)[] = [];
  let disposed = false;

  function track(p: Promise<() => void>) {
    p.then((u) => {
      if (disposed) u();
      else unlisteners.push(u);
    }).catch(() => {
      listenersInitialized = false;
    });
  }

  // Log events
  track(tauriListen("preview:log", (payload) => {
    const { line } = payload as { line: string };
    useLocalPreviewStore.getState().addLog(line);
  }));

  // Port detected
  track(tauriListen("preview:port-detected", (payload) => {
    const { port } = payload as { port: number };
    useLocalPreviewStore.setState({ serverPort: port });
  }));

  // Server stopped
  track(tauriListen("preview:server-stopped", () => {
    useLocalPreviewStore.setState({ serverRunning: false, serverPort: null });
  }));

  // Chat streaming chunks
  track(tauriListen("preview:chat-chunk", (payload) => {
    const { text, done } = payload as { text: string; done: boolean };
    if (done) {
      useLocalPreviewStore.setState({ chatStreaming: false });
      return;
    }
    useLocalPreviewStore.setState((s) => {
      const msgs = [...s.chatMessages];
      const last = msgs[msgs.length - 1];
      if (last?.role === "assistant") {
        msgs[msgs.length - 1] = { ...last, content: last.content + text };
      }
      return { chatMessages: msgs };
    });
  }));

  // Chat done
  track(tauriListen("preview:chat-done", () => {
    useLocalPreviewStore.setState({ chatStreaming: false });
  }));

  return () => {
    disposed = true;
    listenersInitialized = false;
    unlisteners.forEach((u) => u());
  };
}
